import type { Range } from './common';

export enum VenueType {
  TIME_BOOKING = 'TIME_BOOKING',
  TICKET_BOOKING = 'TICKET_BOOKING'
}

export interface Resource {
  id?: string;
  name: string;
}

// export type Resources<DetailsRequired extends boolean = false> = {
//   count: number;
// } & (DetailsRequired extends true ? { details: Array<Resource> } : { details?: Array<Resource> });

// type ResourcesWithDetails = Resources<true>;
/**
 * For Time Booking we could have details or no details, because court booking would have details but table booking would not have details
 */
export interface Resources {
  count: number;
  details?: Array<Resource>;
}

export interface BasePackage {
  id?: string;
  title: string;
  description?: string;
  price: number;
  // If Provided, Should be a number between 0 and 100
  discount?: number;
  resources: Resources;
  pauseBooking?: boolean;
}

enum TimeBasedPackageType {
  FIXED_TIME = 'FIXED_TIME',
  VARIABLE_TIME = 'VARIABLE_TIME'
}

// Package is linked to a venue right now. So it is possible to have multiple prices for different venue
// Drawback is multiprice for different slots and a way to list them in the prop dashboard products page
// Limit to Single slot until this drawback is resolved
export type TicketBookingPackage = BasePackage;

export interface TicketBookingPackageWithoutResources extends TicketBookingPackage {
  resourcesToBook: number;
  resources: never;
}

export interface TimeBookingPackage extends BasePackage {
  // isPerPersonPrice?: boolean; // Need this for swimming and bowling
  // isPerPersonDuration?: boolean; // Need this for bowling and may be for table booking probably
  duration: number; // in minutes
  resourcesPerBooking?: Range<number>;
}

export type Package = TicketBookingPackage | TicketBookingPackageWithoutResources | TimeBookingPackage;

export type Batch = Range<Date>;
export type Session = Array<Batch>;
export type Sessions = Array<Session>;

/**
 * #### Description
 *
 * A Slot could be regarding mutiday workshop e.g. For 2 days coding workshop:
 *  - Slot 1
 *    - Session 1: `19th: Java`
 *      - Batch 1: 3PM
 *      - Batch 2: 9PM
 *    - Session 2: `20th: Python`
 *      - Batch 1: 3PM
 *      - Batch 2: 9PM
 *  - Slot 2
 *    - Session 1: `25th: Java`
 *      - Batch 1: 3PM
 *      - Batch 2: 9PM
 *    - Session 2: `26th: Python`
 *      - Batch 1: 3PM
 *      - Batch 2: 9PM
 *
 * #### Validations:
 *  - All Sessions in a Slot should mandatorily have same number of batches. Othewise it would add inconsistency in bookings.
 *  i.e. lets say session 1 has 2 batch of 50 seats each but session 2 only has 1 slot with 50 seats then inconsistency would be there.
 * Note:
 *  - package[*].resources is the resources available for a pariticular batch.
 *  - pricing is associated with a slot and not a session or batch.
 *  - package[*].resources.count is the total number of resources available for a batch which would be same for all th batches in a slot.
 */
export interface Slot {
  id?: string;
  sessions: Sessions;
  pauseBooking?: boolean;
  isLive?: boolean;
}

export interface SlotWithResources extends Slot {
  resources: Resources;
}

export type SlotType = Slot | SlotWithResources;

export interface BaseVenue {
  id?: string;
  propId: string; // For Auth.. Only this prop should be able to modify this venue
  listingId: string;
  title: string;
  phoneNumber: string;
  address: string;
  location: string; // GMapas Link
  city: string;
  venueType: VenueType;
  pauseBooking?: boolean;
}

export interface TicketBookingVenue extends BaseVenue {
  venueType: VenueType.TICKET_BOOKING;
  isResourcesLinkedToVenue?: false;

  slots: Array<Slot>; // e.g. 4 slots for 2 days workshop
  packages: Array<TicketBookingPackage>;
}

export interface TicketBookingVenueWithResources extends BaseVenue {
  venueType: VenueType.TICKET_BOOKING;
  isResourcesLinkedToVenue: true;

  slots: Array<SlotWithResources>; // e.g. 4 slots for 2 days workshop
  packages: Array<TicketBookingPackageWithoutResources>;
}

export enum WeekDay {
  SUNDAY,
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURADY
}

// Minutes after midnight in UTC
// e.g. [{ from: 60 to: 120 }] minutes from starting time of the day
// i.e. 4 AM - 10 PM => 4 * 60 - 22 * 60
export type OpenHour = Range<number>;
type OpenHours = Array<OpenHour>;

export interface TimeBookingVenue extends BaseVenue {
  venueType: VenueType.TIME_BOOKING;
  isResourcesLinkedToVenue?: never;

  openDate: Range<Date>; // e.g. 12th Oct - 13th Oct
  openHours: {
    default: OpenHours;
    // weekday?: Partial<Record<WeekDay, OpenHours>>;
  }; //& Partial<Record<WeekDay, OpenHours>>;
  packages: Array<TimeBookingPackage>;
}

export type Venue = TicketBookingVenue | TicketBookingVenueWithResources | TimeBookingVenue;
